import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getCompanies, logCompanySwitch } from "../api/companies.api";
import { getMe } from "../api/auth.api";
import { setCompanyId as setApiCompanyId } from "../api/client";
import { useAuth } from "../auth/AuthContext";

const CompanyContext = createContext(null);
const STORAGE_KEY = "selectedCompanyId";

function normalizeId(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const text = String(value).trim();

  if (!text || text === "null" || text === "undefined") {
    return null;
  }

  return text;
}

export function CompanyProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [isRealSuperadmin, setIsRealSuperadmin] = useState(false);
  const [adminView, setAdminViewState] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("adminView") === "1";
  });
  const [isWikipediaEmployee, setIsWikipediaEmployee] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(() => {
    if (typeof window === "undefined") {
      return null;
    }

    const storedId = normalizeId(window.localStorage.getItem(STORAGE_KEY));
    setApiCompanyId(storedId);
    return storedId;
  });
  // Default = true, damit RequireCompanyPermission beim ersten Render nach
  // einem Browser-Reload nicht auf `selectedCompany === null` reagiert und den
  // Nutzer auf /dashboard umleitet. Wird in der Effect-Logik unten explizit
  // auf false gesetzt, sobald entweder der Nutzer nicht authentifiziert ist
  // oder refreshCompanies() einmal durchgelaufen ist.
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const selectedCompanyIdRef = useRef(selectedCompanyId);

  useEffect(() => {
    selectedCompanyIdRef.current = selectedCompanyId;
    setApiCompanyId(selectedCompanyId);
  }, [selectedCompanyId]);

  const applySelectedCompany = useCallback((nextId) => {
    const normalizedId = normalizeId(nextId);

    setSelectedCompanyId(normalizedId);
    setApiCompanyId(normalizedId);

    if (typeof window === "undefined") {
      return;
    }

    if (normalizedId) {
      window.localStorage.setItem(STORAGE_KEY, normalizedId);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const selectCompany = useCallback((nextId) => {
    const normalizedId = normalizeId(nextId);
    const previousId = selectedCompanyIdRef.current;

    applySelectedCompany(normalizedId);

    // Nur loggen, wenn echter Nutzerwechsel (nicht beim ersten Setzen oder Reset)
    if (normalizedId && normalizedId !== previousId) {
      logCompanySwitch(normalizedId).catch(() => {
        // Fehler ignorieren — Logging soll Wechsel nie blockieren
      });
    }
  }, [applySelectedCompany]);

  const refreshCompanies = useCallback(
    async (preferredId) => {
      if (!isAuthenticated) {
        return;
      }

      setLoadingCompanies(true);
      setLoadError(null);

      try {
        const data = await getCompanies();
        const list = data.companies || [];
        setCompanies(list);
        setUserRole(data.user_role || null);
        setIsRealSuperadmin(Boolean(data.is_real_superadmin));

        let nextId = normalizeId(preferredId);

        if (!nextId || !list.some((company) => String(company.id) === nextId)) {
          nextId = list.length > 0 ? String(list[0].id) : null;
        }

        applySelectedCompany(nextId);
      } catch (err) {
        setCompanies([]);
        setUserRole(null);
        setIsRealSuperadmin(false);
        applySelectedCompany(null);
        setLoadError(err);
      } finally {
        setLoadingCompanies(false);
      }
    },
    [applySelectedCompany, isAuthenticated]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setCompanies([]);
      setUserRole(null);
      setIsRealSuperadmin(false);
      setLoadError(null);
      setLoadingCompanies(false);
      applySelectedCompany(null);
      return;
    }

    (async () => {
      // Wenn bereits eine Auswahl im Browser gespeichert ist (z.B. nach
      // einem Reload oder erneutem Login nach Auto-Logout mit noch
      // hinterlegtem selectedCompanyId), soll diese erhalten bleiben.
      // Das Lieblings-Unternehmen greift nur, wenn keine Auswahl vorhanden
      // ist – typischerweise direkt nach einem Login (login() löscht den
      // gespeicherten Wert).
      //
      // WICHTIG: getMe() muss IMMER aufgerufen werden, damit Nutzer-Flags
      // wie is_wikipedia_employee hydratisiert werden. Sonst bleibt der
      // Flag nach Auto-Logout + Re-Login auf false, weil der storedId-
      // Zweig zuvor getMe() übersprungen hat.
      const storedId = selectedCompanyIdRef.current;

      let favoriteCompanyId = null;
      try {
        const me = await getMe();
        favoriteCompanyId = normalizeId(me?.favorite_company_id);
        setIsWikipediaEmployee(Boolean(me?.is_wikipedia_employee));
      } catch {
        setIsWikipediaEmployee(false);
      }

      if (storedId) {
        await refreshCompanies(storedId);
      } else {
        await refreshCompanies(favoriteCompanyId ?? "");
      }
    })();
  }, [applySelectedCompany, isAuthenticated, refreshCompanies]);

  const selectedCompany = useMemo(() => {
    if (!selectedCompanyId) {
      return null;
    }

    return companies.find((company) => String(company.id) === selectedCompanyId) || null;
  }, [companies, selectedCompanyId]);

  const setAdminView = useCallback((on) => {
    setAdminViewState(Boolean(on));
    if (typeof window !== "undefined") {
      if (on) {
        window.localStorage.setItem("adminView", "1");
      } else {
        window.localStorage.removeItem("adminView");
      }
    }
  }, []);

  // Admins (Superadmin/Subadmin) sehen Dashboards standardmäßig als Mitglied;
  // erst in der "Adminsicht" greifen die Superadmin-Funktionen.
  const isAdmin = userRole === "superadmin";
  const effectiveUserRole = isAdmin && !adminView ? "viewer" : userRole;

  const value = useMemo(
    () => ({
      companies,
      userRole: effectiveUserRole,
      rawUserRole: userRole,
      isAdmin,
      isRealSuperadmin,
      adminView,
      setAdminView,
      isWikipediaEmployee,
      selectedCompanyId,
      selectedCompany,
      setSelectedCompanyId: selectCompany,
      loadingCompanies,
      loadError,
      refreshCompanies,
    }),
    [
      adminView,
      companies,
      effectiveUserRole,
      isAdmin,
      isRealSuperadmin,
      isWikipediaEmployee,
      loadError,
      loadingCompanies,
      refreshCompanies,
      selectCompany,
      selectedCompany,
      selectedCompanyId,
      setAdminView,
      userRole,
    ]
  );

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompany() {
  const context = useContext(CompanyContext);

  if (!context) {
    throw new Error("useCompany must be used within CompanyProvider");
  }

  return context;
}
